import { createLogger } from "logging";
import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { useCookies } from "react-cookie";
import { germanLabels } from "translations/de";
import { englishLabels } from "translations/en";
import { frenchLabels } from "translations/fr";
import { italianLabels } from "translations/it";
import { TranslationLabels } from "translations/TranslationLabels";

import { ITranslationsContext, Language, TranslationsContext } from "./TranslationsContext";

const logger = createLogger("TranslationsProvider");

const cookieKey = "i18n";

const allLanguages = Object.values(Language);
const browserLanguage = navigator.language.split("-")[0] as Extract<Language, string>;
logger.debug(`browserLanguage:='${browserLanguage}'`);

const defaultLanguage = allLanguages.find((l) => l === browserLanguage) || Language.DE;
logger.debug(`defaultLanguage:='${defaultLanguage}'`);

const labels: Record<Language, TranslationLabels> = {
  [Language.DE]: germanLabels,
  [Language.FR]: frenchLabels,
  [Language.IT]: italianLabels,
  [Language.EN]: englishLabels,
};

export const TranslationsProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const [cookies, setCookie] = useCookies([cookieKey], { doNotParse: true, doNotUpdate: true });
  const cookieValue = cookies[cookieKey];

  const initialLanguage = allLanguages.find((l) => l === cookieValue) || defaultLanguage;
  logger.debug(`render(): cookie:='${cookieValue}', resolved:='${initialLanguage}'`);

  const [currentLanguage, setLanguage] = useState(initialLanguage);

  const changeLanguage = useCallback(
    (newLanguage: Language) => {
      logger.debug(`changeLanguage(): '${currentLanguage}' -> '${newLanguage}'`);
      setLanguage(newLanguage);
      const expirationDate = new Date();
      expirationDate.setFullYear(expirationDate.getFullYear() + 1);
      setCookie(cookieKey, newLanguage, { path: "/", expires: expirationDate });
    },
    [currentLanguage, setCookie, setLanguage]
  );

  useEffect(() => {
    const before = document.documentElement.lang;
    const after = `${currentLanguage}`.toLowerCase();
    if (before != after) {
      logger.debug(`useEffect(): html.lang changed to '${after}' (was: '${before}')`);
      document.documentElement.lang = after;
    }
  }, [currentLanguage]);

  useEffect(() => {
    const before = window.labels;
    const after = labels[currentLanguage];
    if (before != after) {
      logger.debug(`useEffect(): global labels changed to '${currentLanguage}'`);
      window.labels = after;
    }
  }, [currentLanguage]);

  const value: ITranslationsContext = useMemo(() => {
    return {
      ...labels[currentLanguage],
      labels: labels[currentLanguage],
      currentLanguage,
      allLanguages,
      changeLanguage,
    };
  }, [currentLanguage, changeLanguage]);

  return <TranslationsContext.Provider value={value}>{children}</TranslationsContext.Provider>;
};
